import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  EnvelopeSimple,
  MapPin,
  WhatsappLogo,
  ArrowRight,
} from "@phosphor-icons/react";
import SectionLabel from "../components/SectionLabel";
import { COMPANY } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        company: form.company || null,
        message: form.message,
      });
      toast.success("Thanks! We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Failed to submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section
        className="bg-[var(--sifra-ink)] text-white border-b border-[var(--sifra-line-dark)]"
        data-testid="contact-hero"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <SectionLabel dark>Contact</SectionLabel>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1]">
              Let's get your hardware moving.
            </h1>
          </div>
          <div className="md:col-span-4 text-white/70 text-base">
            Share your requirement. A dedicated account manager will get back to
            you within a few business hours.
          </div>
        </div>
      </section>

      <section
        className="bg-[var(--sifra-ink)] text-white"
        data-testid="contact-body"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* FORM */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 border border-[var(--sifra-line-dark)] bg-[#0A0A0A] p-8 md:p-12 space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label="Name"
                name="name"
                value={form.name}
                onChange={onChange}
                testid="contact-input-name"
                required
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                testid="contact-input-email"
                required
              />
              <Field
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={onChange}
                testid="contact-input-phone"
              />
              <Field
                label="Company"
                name="company"
                value={form.company}
                onChange={onChange}
                testid="contact-input-company"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-label uppercase text-white/60 mb-2">
                Message
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={5}
                required
                data-testid="contact-input-message"
                placeholder="Tell us what you need — quantities, models, timelines..."
                className="w-full bg-transparent border border-[var(--sifra-line-dark)] px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="inline-flex items-center gap-2 bg-white text-[var(--sifra-ink)] px-8 py-4 text-xs tracking-label font-semibold uppercase hover:bg-[var(--sifra-accent)] hover:text-white disabled:opacity-60 transition-colors"
            >
              {submitting ? "Sending..." : "Send Enquiry"}{" "}
              <ArrowRight size={14} weight="bold" />
            </button>
          </form>

          {/* DETAILS */}
          <aside className="lg:col-span-5 space-y-6" data-testid="contact-details">
            <Detail
              icon={<Phone size={20} weight="thin" />}
              label="Phone"
              value={
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="text-white hover:text-[var(--sifra-accent)] transition-colors"
                  data-testid="contact-phone"
                >
                  {COMPANY.phone}
                </a>
              }
            />
            <Detail
              icon={<WhatsappLogo size={20} weight="thin" />}
              label="WhatsApp"
              value={
                <a
                  href={COMPANY.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-[#25D366] transition-colors"
                  data-testid="contact-whatsapp"
                >
                  Chat instantly →
                </a>
              }
            />
            {COMPANY.emails.map((e) => (
              <Detail
                key={e}
                icon={<EnvelopeSimple size={20} weight="thin" />}
                label="Email"
                value={
                  <a
                    href={`mailto:${e}`}
                    className="text-white hover:text-[var(--sifra-accent)] transition-colors break-all"
                    data-testid={`contact-email-${e}`}
                  >
                    {e}
                  </a>
                }
              />
            ))}
            <Detail
              icon={<MapPin size={20} weight="thin" />}
              label="Registered Office"
              value={<span className="text-white/80">{COMPANY.offices.registered}</span>}
            />
            <Detail
              icon={<MapPin size={20} weight="thin" />}
              label="Branch Office"
              value={<span className="text-white/80">{COMPANY.offices.branch}</span>}
            />
          </aside>
        </div>
      </section>
    </>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required, testid }) => (
  <div>
    <label className="block text-[10px] tracking-label uppercase text-white/60 mb-2">
      {label}
      {required && <span className="text-[var(--sifra-accent)] ml-1">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full bg-transparent border border-[var(--sifra-line-dark)] px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white transition-colors"
    />
  </div>
);

const Detail = ({ icon, label, value }) => (
  <div className="border border-[var(--sifra-line-dark)] p-6 flex items-start gap-4">
    <div className="text-white/60 mt-0.5">{icon}</div>
    <div>
      <div className="text-[10px] tracking-label uppercase text-white/50 mb-1">
        {label}
      </div>
      <div className="text-sm">{value}</div>
    </div>
  </div>
);

export default Contact;
